import React from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, ChevronRight, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGetJson as apiGet } from '../../../../utils/api';

// "Latest News" panel for the landing. Shows recent articles with their JPRO
// Take one-liner. Blue palette (no indigo). Self-hides on error/empty.
const OverviewNews = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['news-widget'],
    queryFn: () => apiGet('/news/articles?limit=8'),
  });

  if (error) return null;
  const articles = data?.articles || [];
  if (!isLoading && articles.length === 0) return null;

  return (
    <section className="h-full flex flex-col rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900/40 overflow-hidden">
      <header className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Newspaper size={14} className="text-blue-600 dark:text-blue-400" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
            Latest News
          </span>
        </div>
        <Link
          to="/dashboard/news"
          className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          See all <ChevronRight size={12} />
        </Link>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
      {isLoading ? (
        <div className="p-5 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse h-10 bg-gray-100 dark:bg-gray-800/60 rounded-lg" />
          ))}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {articles.map((a) => (
            <li key={a.id}>
              <Link
                to="/dashboard/news"
                className="block px-5 py-3 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors group"
              >
                <div className="flex items-center gap-2 text-[10.5px] text-gray-500 dark:text-gray-400 mb-1">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                    {a.source?.organization || a.source?.name}
                  </span>
                  <span className="opacity-50">·</span>
                  <span className="inline-flex items-center gap-0.5">
                    <Clock size={9} /> {formatRelative(a.published_at)}
                  </span>
                </div>
                <h4 className="text-[13.5px] font-medium font-rasa text-gray-900 dark:text-white leading-snug line-clamp-2 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                  {a.title}
                </h4>
                {a.jpro_take && (
                  <p className="mt-1 text-[12px] leading-snug text-blue-700/90 dark:text-blue-300/90 line-clamp-1">
                    <span className="font-semibold">JPRO Take:</span> {a.jpro_take}
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      </div>
    </section>
  );
};

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export default OverviewNews;
